import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  FormGroup,
  Radio,
  SelectionControlLabel
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { ProductListItem } from './ProductListItem';
import { CustomSkeletonLine } from './CustomSkeletonLine';
import { LAUNDRY_ACCESSORIES_DRAWER, NO_ACCESSORIES } from '../../constants/index';
import { InfoDrawer } from './infoDrawer/InfoDrawer';

const AccessoryListContent = ({ accessories, loading, addAccessories }) => {
  const [selectedOffering, setSelectedOffering] = useState(NO_ACCESSORIES);

  const { configService = {} } = useContext(ExperienceContext) || {};

  const configIsAccessoriesFeatureEnabled = useConfigService('fs:isAccessoriesFeatureEnabled') || null;
  const enableAccessories = configIsAccessoriesFeatureEnabled || configService['fs:isAccessoriesFeatureEnabled'];
  if (!enableAccessories) {
    return null;
  }

  if (loading) {
    // TODO: Address any CLS concerns with an appropriate placeholder
    return (
      <div
        className="sui-flex sui-flex-col sm:sui-mx-4"
        data-testid="accessory-list-placeholder"
      >
        <CustomSkeletonLine height="25px" />
      </div>
    );
  }

  if (!accessories || accessories.length === 0) {
    return null;
  }

  const accessoriesData = accessories[0];

  const handleAccessoryChange = (event) => {
    const selectedAccessory = event?.target?.value;
    const isNoAccessoriesSelected = selectedAccessory === NO_ACCESSORIES;
    setSelectedOffering(isNoAccessoriesSelected ? NO_ACCESSORIES : `offering-${selectedAccessory}`);
    const accesoriesData = isNoAccessoriesSelected ? null : accessoriesData?.offerings?.[selectedAccessory];
    addAccessories(accesoriesData);
  };

  return (
    <div className="sui-bg-white" data-testid="accessory-list-content">
      <div className="sui-pt-8">

        {/* Title Bar */}
        {/* TODO: Refactor and componentize */}
        <div className="sui-flex sui-items-center sui-mb-4">
          <h3 className="sui-text-lg sui-font-semibold sui-text-gray-900">
            {accessoriesData.categoryName}
          </h3>
          <div className="sui-ml-2 sui-w-4 sui-h-4 sui-border sui-border-gray-400 sui-rounded-full
                sui-flex sui-items-center sui-justify-center"
          >
            <InfoDrawer variantName={LAUNDRY_ACCESSORIES_DRAWER} />
          </div>
        </div>

        {/* Accessory Options */}
        <FormGroup gap className="sui-space-y-4">
          {accessoriesData.offerings?.map((offering, offeringIndex) => (
            <div
              key={offeringIndex}
              className="sui-rounded-base sui-p-4 sui-border-solid sui-border-b-1 sui-border-1 sui-border-primary"
            >
              <SelectionControlLabel label={offering.offeringName}>
                <Radio
                  checked={selectedOffering === `offering-${offeringIndex}`}
                  onChange={handleAccessoryChange}
                  value={offeringIndex}
                  name="accessory-selection"
                  inputAttributes={{ 'aria-label': offering.offeringName }}
                />
              </SelectionControlLabel>

              {/* AccessoryListProductPods */}
              {offering.products?.map((product, productIndex) => {
                return (
                  <div key={`accessory-${product.itemId}`}>
                    <ProductListItem
                      product={product}
                      variant="accessory"
                    />

                    {offering.products.length > 1 && productIndex < offering.products.length - 1 && (
                      <div className="sui-flex sui-justify-center sui-py-2">
                        <div
                          className="sui-w-8 sui-h-8 sui-bg-gray-100 sui-rounded-full
                            sui-flex sui-items-center sui-justify-center"
                        >
                          <span className="sui-text-lg sui-font-bold sui-text-gray-600">+</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* No Accessories Option */}
          <div className="sui-rounded-base sui-p-4 sui-border-solid sui-border-1 sui-border-primary">
            <SelectionControlLabel label="I don't need accessories right now.">
              <Radio
                checked={selectedOffering === NO_ACCESSORIES}
                onChange={handleAccessoryChange}
                value={NO_ACCESSORIES}
                name="accessory-selection"
                inputAttributes={{ 'aria-label': 'No accessories needed' }}
              />
            </SelectionControlLabel>
          </div>
        </FormGroup>
      </div>
    </div>
  );
};

AccessoryListContent.propTypes = {
  accessories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      offerings: PropTypes.arrayOf(
        PropTypes.shape({
          offeringName: PropTypes.string.isRequired,
          products: PropTypes.arrayOf(PropTypes.shape({
            itemId: PropTypes.string,
            quantity: PropTypes.number,
            type: PropTypes.string
          }))
        })
      )
    })
  ),
  loading: PropTypes.bool.isRequired,
  addAccessories: PropTypes.func.isRequired
};

AccessoryListContent.defaultProps = {
  accessories: []
};

AccessoryListContent.dataModel = extend(ProductListItem);

export { AccessoryListContent };
